import { useState } from 'react';
import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faSearch, faTriangleExclamation } from '@/fontawesome';
import SymbolSourceBadge from '@/components/admin/common/SymbolSourceBadge';
import RefCheckIndicator from '@/components/admin/symbols/RefCheckIndicator';
import { SYMBOL_DATA_SOURCE_LABELS } from '@/models';
import type { SymbolSourceRef } from '@/models';
import type { VerifySymbolEODHDResult, VerifySymbolSourceResult } from '@/modelsAPI';
import type { SymbolSourceRefs } from '@/hooks/useSymbolSourceRefs';
import {
  formatEODHDInstrument,
  normalizeSourceRef,
  refCheckDescription,
  refCheckState,
  symbolSourceTooltip,
} from '@/utils/symbol-source';
import type { RefCheckState, SymbolPriceSource } from '@/utils/symbol-source';

const ACCENT_CLASSES = {
  cyan: 'focus:ring-cyan-500 focus:border-cyan-500',
  blue: 'focus:ring-blue-500 focus:border-blue-500',
} as const;

interface Props {
  refs: SymbolSourceRefs;
  /** Ticker and exchange are set, so the refs can be verified. */
  canVerify: boolean;
  disabled: boolean;
  accent: keyof typeof ACCENT_CLASSES;
  /** Stored refs of the symbol being edited; their status is shown next to the labels. */
  current?: { googleSheets: SymbolSourceRef; eodhd: SymbolSourceRef };
  notice?: ReactNode;
}

/** Google-Sheets and EODHD ref inputs with auto-detection, verification and EODHD search hits. */
export default function SymbolSourceRefFields({ refs, canVerify, disabled, accent, current, notice }: Props) {
  const [showCandidates, setShowCandidates] = useState(false);

  const result = refs.result;
  const gsState = refCheckState('GSHEETS', refs.googleSheetsRef, result?.googleSheets ?? null, refs.verifying);
  const eodhdState = refCheckState('EODHD', refs.eodhdRef, result?.eodhd ?? null, refs.verifying);

  const candidates = result?.eodhd?.candidates ?? [];
  const eodhdUsable = eodhdState.kind === 'found' || eodhdState.kind === 'candidate';
  const candidatesOpen = !refs.verifying && candidates.length > 0 && (!eodhdUsable || showCandidates);

  const bothEmpty = normalizeSourceRef(refs.googleSheetsRef) === '' && normalizeSourceRef(refs.eodhdRef) === '';
  const actionDisabled = !canVerify || disabled || refs.verifying;
  const buttonClass = 'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer';

  return (
    <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Datenquellen</h3>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={refs.detect}
            disabled={actionDisabled}
            className={buttonClass}
            title={canVerify ? 'Referenzen aus Ticker und Börse ableiten und beide Felder überschreiben' : 'Ticker und Börse erforderlich'}
          >
            <FontAwesomeIcon icon={faBolt} className="w-3 h-3" />
            Automatisch ermitteln
          </button>
          <button
            type="button"
            onClick={refs.check}
            disabled={actionDisabled || !refs.refsValid}
            className={buttonClass}
            title={canVerify ? 'Die eingetragenen Referenzen bei den Quellen prüfen' : 'Ticker und Börse erforderlich'}
          >
            <FontAwesomeIcon icon={faSearch} className="w-3 h-3" />
            Prüfen
          </button>
        </div>
      </div>

      {notice !== undefined && (
        <p className="mb-3 text-xs text-gray-600 dark:text-gray-400">{notice}</p>
      )}

      {result !== null && result.reason === 'EXISTING_ENTITY' && (
        <p className="mb-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          {current !== undefined
            ? 'Es gibt noch ein weiteres Symbol mit diesem Ticker (Duplikat?).'
            : 'Für diesen Ticker existiert bereits ein Symbol.'}
        </p>
      )}
      {result !== null && result.reason === 'INVALID_TICKER' && (
        <p className="mb-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-400">
          Der Ticker ist ungültig.
        </p>
      )}

      <div className="space-y-4">
        <RefInput
          id="googleSheetsRef"
          source="GSHEETS"
          label="Google-Sheets-Referenz"
          placeholder="z.B. ETR:SAP"
          value={refs.googleSheetsRef}
          onChange={refs.setGoogleSheetsRef}
          state={gsState}
          sourceResult={result?.googleSheets ?? null}
          detectionPending={refs.detectionPending}
          current={current?.googleSheets}
          disabled={disabled}
          accent={accent}
        />

        <div>
          <RefInput
            id="eodhdRef"
            source="EODHD"
            label="EODHD-Referenz"
            placeholder="z.B. SAP.XETRA"
            value={refs.eodhdRef}
            onChange={refs.setEodhdRef}
            state={eodhdState}
            sourceResult={result?.eodhd ?? null}
            detectionPending={refs.detectionPending}
            current={current?.eodhd}
            disabled={disabled}
            accent={accent}
          />

          {!refs.verifying && candidates.length > 0 && eodhdUsable && (
            <button
              type="button"
              onClick={() => setShowCandidates(!showCandidates)}
              className="mt-1 text-xs text-cyan-700 dark:text-cyan-400 hover:underline cursor-pointer"
            >
              {showCandidates ? 'Alternativen ausblenden' : `Alternativen anzeigen (${candidates.length})`}
            </button>
          )}

          {candidatesOpen && (
            <div className="mt-2">
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">Treffer der EODHD-Suche:</p>
              <ul className="max-h-40 overflow-y-auto overscroll-contain rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                {candidates.map(candidate => {
                  const selected = normalizeSourceRef(candidate.ref) === normalizeSourceRef(refs.eodhdRef);
                  return (
                    <li key={candidate.ref}>
                      <button
                        type="button"
                        onClick={() => refs.setEodhdRef(candidate.ref)}
                        disabled={disabled}
                        className={`w-full px-3 py-1.5 text-left text-xs cursor-pointer disabled:cursor-not-allowed ${
                          selected
                            ? 'bg-cyan-50 dark:bg-cyan-900/30'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="font-mono font-medium text-gray-900 dark:text-white">{candidate.ref}</span>
                        <span className="text-gray-500 dark:text-gray-400"> — {formatEODHDInstrument(candidate)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      {bothEmpty && !refs.detectionPending && !refs.verifying && (
        <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
          <FontAwesomeIcon icon={faTriangleExclamation} className="w-3 h-3 mr-1" />
          Ohne Referenz erhält das Symbol keine Kursdaten.
        </p>
      )}
    </div>
  );
}

interface RefInputProps {
  id: string;
  source: SymbolPriceSource;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  state: RefCheckState;
  sourceResult: VerifySymbolSourceResult | VerifySymbolEODHDResult | null;
  detectionPending: boolean;
  current: SymbolSourceRef | undefined;
  disabled: boolean;
  accent: keyof typeof ACCENT_CLASSES;
}

function RefInput({ id, source, label, placeholder, value, onChange, state, sourceResult, detectionPending, current, disabled, accent }: RefInputProps) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <label htmlFor={id} className="block text-sm text-gray-700 dark:text-gray-300">
          {label}
        </label>
        {current !== undefined && (
          <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            Aktuell:
            <SymbolSourceBadge status={current.status} title={symbolSourceTooltip(SYMBOL_DATA_SOURCE_LABELS[source], current)} />
          </span>
        )}
      </div>
      <input
        type="text"
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm font-mono text-sm focus:outline-none dark:bg-gray-700 dark:text-white ${ACCENT_CLASSES[accent]}`}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
      />
      <RefCheckIndicator source={source} state={state} text={refCheckDescription(source, state, sourceResult, detectionPending)} />
    </div>
  );
}
